import {
  FileText,
  Sparkles,
  UserCircle,
  ShieldCheck,
  Crown,
  Bot,
  AlertTriangle,
  Gavel,
  RefreshCw,
  Mail,
  Scale,
  BookOpen,
  Smartphone,
  CreditCard,
} from 'lucide-react';
import { LegalPageLayout } from '../components/legal/LegalPageLayout';

const sections = [
  { id: 'introduction', label: 'Introduction', icon: <FileText className="w-4 h-4" /> },
  { id: 'beta-notice', label: 'Beta Notice', icon: <Sparkles className="w-4 h-4" /> },
  { id: 'service', label: 'The Service', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'account', label: 'Account Requirements', icon: <UserCircle className="w-4 h-4" /> },
  { id: 'conduct', label: 'Acceptable Use', icon: <ShieldCheck className="w-4 h-4" /> },
  { id: 'ip', label: 'Intellectual Property', icon: <Crown className="w-4 h-4" /> },
  { id: 'srd', label: 'SRD Attribution', icon: <BookOpen className="w-4 h-4" /> },
  { id: 'ai-content', label: 'AI-Generated Content', icon: <Bot className="w-4 h-4" /> },
  { id: 'purchases', label: 'Tokens & In-App Purchases', icon: <CreditCard className="w-4 h-4" /> },
  { id: 'mobile', label: 'Mobile Apps & Stores', icon: <Smartphone className="w-4 h-4" /> },
  { id: 'termination', label: 'Termination', icon: <AlertTriangle className="w-4 h-4" /> },
  { id: 'disclaimers', label: 'Disclaimers', icon: <ShieldCheck className="w-4 h-4" /> },
  { id: 'liability', label: 'Limitation of Liability', icon: <Scale className="w-4 h-4" /> },
  { id: 'indemnification', label: 'Indemnification', icon: <Gavel className="w-4 h-4" /> },
  { id: 'disputes', label: 'Disputes', icon: <Gavel className="w-4 h-4" /> },
  { id: 'changes', label: 'Changes', icon: <RefreshCw className="w-4 h-4" /> },
  { id: 'contact', label: 'Contact', icon: <Mail className="w-4 h-4" /> },
];

const h2 = 'text-2xl font-semibold text-foreground mt-10';
const h3 = 'text-lg font-semibold text-foreground mt-4';
const body = 'text-base leading-relaxed text-foreground/80';
const list = 'list-disc space-y-2 pl-6 text-foreground/80';
const link = 'text-primary hover:underline';

export default function TermsOfService() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="Last Updated: May 20, 2026"
      sections={sections}
    >
      <div className="space-y-6">
        <section id="introduction" className="space-y-4 scroll-mt-8">
          <p className={body}>
            Welcome to Polymyth. These Terms of Service ("Terms") govern your access to and use of
            the Polymyth platform at{' '}
            <a href="https://polymyth.quest" className={link}>
              polymyth.quest
            </a>
            , the Polymyth mobile applications for iOS and Android (the "Mobile Apps"), and related
            services (together, the "Service"). By creating an account, downloading the Mobile Apps,
            or otherwise using the Service, you agree to these Terms. If you don't agree, don't use
            the Service.
          </p>
        </section>

        <section id="beta-notice" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>1. Beta Notice</h2>
          <div className="rounded-lg border border-accent-gold/40 bg-accent-gold/5 p-4">
            <p className={body}>
              <strong>Polymyth is currently in beta.</strong> The Service is pre-release,
              experimental, and free to start. During beta:
            </p>
            <ul className={`${list} mt-3`}>
              <li>Features may change, break, or be removed without notice.</li>
              <li>
                Player data — including characters, sessions, journals, and generated narratives —
                may be wiped, reset, or migrated at any time.
              </li>
              <li>We make no availability guarantees. The Service may be offline or degraded.</li>
              <li>
                We may end the beta, your access, or the Service entirely at any time for any
                reason.
              </li>
              <li>
                Beta players receive a limited allocation of in-game tokens as a gift. Additional
                tokens may be purchased through Apple App Store or Google Play in-app purchases on
                the Mobile Apps; web-based purchases are not currently offered.
              </li>
            </ul>
          </div>
        </section>

        <section id="service" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>2. The Service</h2>
          <p className={body}>
            Polymyth is an AI-powered role-playing game platform inspired by fifth edition tabletop
            game mechanics. Players create characters, join adventure modules, and play sessions in
            which an AI game master narrates the story, resolves mechanics, and voices non-player
            characters. The AI is powered by large language models provided by third parties (see
            Section 8).
          </p>
          <p className={body}>
            A party may hold up to four members. Those can be players who join each other, or
            AI-controlled companion characters met during an adventure, in any combination.
          </p>
        </section>

        <section id="account" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>3. Account Requirements</h2>
          <ul className={list}>
            <li>You must be at least 13 years old to create an account and use the Service.</li>
            <li>
              If you are between 13 and the age of majority in your jurisdiction, you represent that
              a parent or legal guardian has reviewed and agreed to these Terms on your behalf.
            </li>
            <li>You must provide accurate information when creating your account.</li>
            <li>
              You are responsible for keeping your credentials (password, passkeys, email) secure.
            </li>
            <li>
              You may not share your account, sell access, or let anyone else use your account.
            </li>
            <li>
              You must notify us at{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>{' '}
              if you suspect unauthorized access.
            </li>
            <li>One account per person. We may suspend duplicate or abusive accounts.</li>
          </ul>
        </section>

        <section id="conduct" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>4. Acceptable Use</h2>
          <p className={body}>You agree not to, and not to attempt to:</p>
          <ul className={list}>
            <li>Use the Service for any illegal purpose or in violation of any applicable law.</li>
            <li>
              Submit, generate, or solicit content that is illegal, harassing, hateful, threatening,
              sexually explicit involving minors, or that incites violence.
            </li>
            <li>Impersonate another person or misrepresent your affiliation with anyone.</li>
            <li>
              Upload content that infringes the intellectual property or privacy rights of a third
              party.
            </li>
            <li>
              Attempt to jailbreak, prompt-inject, or otherwise manipulate the AI into violating
              these Terms, its safety policies, or applicable law.
            </li>
            <li>
              Exploit, cheat, automate, or reverse-engineer the Service; probe for vulnerabilities
              without explicit written permission; or scrape the Service.
            </li>
            <li>
              Use the Service to train, benchmark, or evaluate any competing AI system or model.
            </li>
            <li>
              Interfere with or disrupt the Service, our infrastructure, or other players' use of
              it.
            </li>
            <li>
              Abuse or overuse the Service in ways that burden our LLM quota, storage, or bandwidth.
            </li>
          </ul>
          <p className={body}>
            We may remove content and suspend or terminate accounts that violate this section. We
            cooperate with law enforcement where required.
          </p>
        </section>

        <section id="ip" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>5. Intellectual Property</h2>

          <h3 className={h3}>Polymyth Owns the Platform</h3>
          <p className={body}>
            The Polymyth name, logos, branding, software, game engine, adventure modules, artwork,
            UI, and all other materials we create are owned by Polymyth and protected by
            intellectual property laws. Nothing in these Terms grants you any right, title, or
            interest in the platform or its content, except the limited license to access and play
            the Service in accordance with these Terms.
          </p>

          <h3 className={h3}>Your Inputs — You Own Them</h3>
          <p className={body}>
            When you play, you provide text, choices, character names, backstories, and other
            content you write ("Your Inputs"). You keep ownership of Your Inputs. By submitting them
            to the Service, you grant Polymyth a worldwide, royalty-free, perpetual, irrevocable,
            sublicensable, transferable license to use, store, reproduce, modify, display,
            distribute, and create derivative works from Your Inputs in order to:
          </p>
          <ul className={list}>
            <li>operate and provide the Service to you and other players;</li>
            <li>generate AI narratives and game content based on your inputs;</li>
            <li>
              debug, evaluate, and improve the Service (including prompt engineering and internal
              model evaluation);
            </li>
            <li>
              feature excerpts for marketing or community purposes, with reasonable anonymization of
              identifying details unless you've given explicit permission.
            </li>
          </ul>
          <p className={body}>
            You represent that Your Inputs do not infringe the rights of any third party and that
            you have the authority to grant this license. You can stop the Service from making
            further use of Your Inputs by deleting your account, though copies may remain in backups
            and logs as described in the Privacy Policy.
          </p>

          <h3 className={h3}>AI-Generated Content — Polymyth Owns It</h3>
          <p className={body}>
            The narratives, dialogue, scene descriptions, NPC speech, combat outcomes, journal
            entries, and other content generated by our AI systems in response to your gameplay
            ("Generated Content") are owned by Polymyth. Generated Content is produced by our
            models, prompts, and infrastructure, and is made available to you as part of the
            Service.
          </p>
          <p className={body}>
            You receive a personal, non-exclusive, non-transferable, revocable license to view and
            enjoy Generated Content from your sessions. You may share screenshots and short excerpts
            of your gameplay for non-commercial personal use — social media posts, fan communities,
            talking about your playthrough — with attribution to Polymyth. You may not commercialize
            Generated Content, republish it at length, or use it to train or build competing AI
            services.
          </p>

          <h3 className={h3}>Respect for Your Characters</h3>
          <p className={body}>
            We recognize that players form real attachments to their characters and the stories they
            create. We will not assert ownership rights against good-faith personal, non-commercial
            creative use of your own characters and sessions — fan art, writing about your
            playthrough, sharing memorable moments with friends. Be a good sport and we'll be one
            too.
          </p>

          <h3 className={h3}>Feedback</h3>
          <p className={body}>
            If you send us suggestions, bug reports, feature requests, or other feedback, you grant
            Polymyth the right to use that feedback freely, without obligation or compensation to
            you.
          </p>

          <h3 className={h3}>Beta Data Usage</h3>
          <p className={body}>
            During beta, we may use Your Inputs, Generated Content, gameplay telemetry, and related
            data to operate, debug, evaluate, and improve the Service and our prompts, mechanics,
            and models. This includes internal review by Polymyth staff and contractors. We may
            revise this practice for general availability; if so, we will update these Terms.
          </p>
        </section>

        <section id="srd" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>6. SRD 5.2 Attribution</h2>
          <p className={body}>
            Certain rules content in Polymyth derives from the System Reference Document 5.2.1 (SRD
            5.2.1) published by Wizards of the Coast LLC, used under the{' '}
            <a
              href="https://creativecommons.org/licenses/by/4.0/legalcode"
              target="_blank"
              rel="noopener noreferrer"
              className={link}
            >
              Creative Commons Attribution 4.0 International License
            </a>
            . Polymyth's use of SRD 5.2.1 is independent of and not endorsed by Wizards of the
            Coast. All other game content, mechanics, and narrative material unique to Polymyth is
            owned by Polymyth and is not covered by that license.
          </p>
        </section>

        <section id="ai-content" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>7. AI-Generated Content Disclaimer</h2>
          <p className={body}>
            Polymyth uses large language models (primarily Google Gemini) to generate narrative,
            dialogue, and game content in real time. By using the Service, you acknowledge that:
          </p>
          <ul className={list}>
            <li>
              AI outputs are non-deterministic and may be inaccurate, inconsistent, offensive,
              biased, or factually wrong.
            </li>
            <li>
              AI outputs may not reflect Polymyth's views or values, and should not be relied upon
              for legal, medical, financial, or safety-critical advice of any kind.
            </li>
            <li>
              We apply reasonable content moderation, but cannot guarantee that every AI output will
              be safe or appropriate. Report problems to{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
              .
            </li>
            <li>
              Your Inputs are sent to third-party AI providers (see the Privacy Policy) under their
              enterprise terms. Do not submit personal, confidential, sensitive, or proprietary
              information to the Service.
            </li>
            <li>
              We may moderate, edit, or remove AI outputs or restrict features to enforce these
              Terms.
            </li>
          </ul>
        </section>

        <section id="purchases" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>8. Tokens & In-App Purchases</h2>
          <p className={body}>
            Polymyth uses an in-game token economy ("PMT" or "tokens") to meter AI-generated actions
            such as rounds of play. Tokens are a limited, personal, non-transferable license to
            access AI-generated content within the Service. Tokens have no monetary value, cannot be
            redeemed for cash, cannot be transferred between accounts, and expire when your account
            is closed or the Service is discontinued.
          </p>
          <p className={body}>
            On the Mobile Apps, you may purchase additional tokens through Apple App Store in-app
            purchase (iOS) or Google Play Billing (Android). All such purchases are processed by
            Apple or Google under their terms; Polymyth does not collect or store your payment
            details. Prices, taxes, and currency are shown at the point of purchase and may vary by
            region.
          </p>
          <p className={body}>
            <strong>Refunds.</strong> Because purchases are processed by Apple or Google, refund
            requests for in-app purchases must be submitted to the relevant store under its policies
            (
            <a
              href="https://support.apple.com/HT204084"
              target="_blank"
              rel="noopener noreferrer"
              className={link}
            >
              Apple
            </a>{' '}
            ·{' '}
            <a
              href="https://support.google.com/googleplay/answer/2479637"
              target="_blank"
              rel="noopener noreferrer"
              className={link}
            >
              Google
            </a>
            ). Where required by applicable consumer-protection law, you retain any non-waivable
            statutory refund rights. Polymyth may, at its discretion, credit tokens to compensate
            for verified Service outages.
          </p>
          <p className={body}>
            We may change token prices, bundle sizes, or the rate at which actions consume tokens.
            We will not retroactively change the credited balance of tokens you have already
            received.
          </p>
        </section>

        <section id="mobile" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>9. Mobile Apps, App Stores & Apple EULA Terms</h2>
          <p className={body}>
            The Mobile Apps are licensed, not sold, to you. Subject to your compliance with these
            Terms, Polymyth grants you a limited, personal, non-exclusive, non-transferable,
            revocable license to install and use the Mobile Apps on devices you own or control.
          </p>
          <p className={body}>
            If you obtained the Mobile App from the Apple App Store, the following additional terms
            apply, as required by Apple:
          </p>
          <ul className={list}>
            <li>
              These Terms are an agreement between you and Polymyth only, not with Apple. Apple is
              not responsible for the Mobile App or its content.
            </li>
            <li>
              Your license to use the iOS Mobile App is limited to use on Apple-branded devices you
              own or control, as permitted by the Apple Media Services Terms and Conditions and the
              Usage Rules in Apple's standard EULA (
              <a
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                target="_blank"
                rel="noopener noreferrer"
                className={link}
              >
                apple.com/legal/internet-services/itunes/dev/stdeula
              </a>
              ).
            </li>
            <li>
              Apple has no obligation to provide maintenance or support for the Mobile App. Polymyth
              is solely responsible for any product warranties, to the extent not effectively
              disclaimed.
            </li>
            <li>
              Polymyth, not Apple, is responsible for addressing any claims by you or any third
              party relating to the Mobile App, including product liability claims, claims that the
              Mobile App fails to conform to any applicable legal or regulatory requirement, and
              claims arising under consumer protection or similar legislation.
            </li>
            <li>
              In the event of any third-party claim that the Mobile App or your possession and use
              of it infringes that third party's intellectual property rights, Polymyth, not Apple,
              will be solely responsible for the investigation, defense, settlement, and discharge
              of any such claim.
            </li>
            <li>
              You represent that you are not located in a country subject to a U.S. Government
              embargo or designated as a "terrorist supporting" country, and that you are not on any
              U.S. Government list of prohibited or restricted parties.
            </li>
            <li>
              Apple and its subsidiaries are third-party beneficiaries of these Terms with respect
              to the iOS Mobile App and, upon your acceptance, will have the right to enforce these
              Terms against you.
            </li>
          </ul>
          <p className={body}>
            If you obtained the Mobile App from Google Play, your use is also subject to the Google
            Play Terms of Service (
            <a
              href="https://play.google.com/intl/en_us/about/play-terms/"
              target="_blank"
              rel="noopener noreferrer"
              className={link}
            >
              play.google.com/about/play-terms
            </a>
            ).
          </p>
        </section>

        <section id="termination" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>10. Termination</h2>
          <h3 className={h3}>By You</h3>
          <p className={body}>
            You may stop using the Service at any time and request account deletion from account
            settings or by emailing{' '}
            <a href="mailto:tavian@polymyth.quest" className={link}>
              tavian@polymyth.quest
            </a>
            . We will delete personal data as described in the Privacy Policy.
          </p>
          <h3 className={h3}>By Polymyth</h3>
          <p className={body}>
            We may suspend or terminate your access at any time, with or without notice, if we
            believe you have violated these Terms, if we are required to by law, or for any other
            reason in our reasonable discretion — including ending the beta entirely. On
            termination, your right to use the Service stops immediately; sections that by their
            nature should survive (including IP, disclaimers, liability, indemnification, disputes,
            and governing law) will survive.
          </p>
        </section>

        <section id="disclaimers" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>11. Disclaimers</h2>
          <p className={`${body} uppercase tracking-tight`}>
            The Service is provided "as is" and "as available" without warranties of any kind,
            whether express, implied, statutory, or otherwise. To the fullest extent permitted by
            law, Polymyth disclaims all warranties, including merchantability, fitness for a
            particular purpose, non-infringement, accuracy, and uninterrupted or error-free
            operation. Polymyth does not warrant that the Service, AI outputs, or any content will
            meet your requirements or expectations.
          </p>
        </section>

        <section id="liability" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>12. Limitation of Liability</h2>
          <p className={body}>
            To the fullest extent permitted by law, Polymyth and its affiliates, officers,
            employees, agents, and licensors shall not be liable for any indirect, incidental,
            special, consequential, exemplary, or punitive damages, including loss of profits, data,
            goodwill, game progress, characters, or in-game items, arising out of or in connection
            with these Terms or the Service, whether based in contract, tort, negligence, strict
            liability, or otherwise, even if advised of the possibility of such damages.
          </p>
          <p className={body}>
            To the fullest extent permitted by law, Polymyth's aggregate liability to you for any
            and all claims arising out of or related to these Terms or the Service will not exceed
            one hundred U.S. dollars ($100). Because beta access is free, you acknowledge this limit
            is reasonable in relation to the non-commercial nature of your use.
          </p>
        </section>

        <section id="indemnification" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>13. Indemnification</h2>
          <p className={body}>
            You agree to defend, indemnify, and hold harmless Polymyth and its affiliates, officers,
            employees, and agents from and against any claims, liabilities, damages, losses, and
            expenses (including reasonable attorneys' fees) arising out of or connected with: (a)
            your use or misuse of the Service; (b) your Inputs; (c) your violation of these Terms or
            any applicable law; or (d) your violation of any third-party right, including
            intellectual property or privacy rights.
          </p>
        </section>

        <section id="disputes" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>14. Dispute Resolution</h2>
          <p className={body}>
            We prefer to resolve disputes informally. Before filing any claim, contact us at{' '}
            <a href="mailto:tavian@polymyth.quest" className={link}>
              tavian@polymyth.quest
            </a>{' '}
            and give us a reasonable opportunity to resolve the issue.
          </p>
          <p className={body}>
            Any claim not resolved informally will be resolved by binding individual arbitration
            administered by a neutral arbitrator under the rules of a recognized arbitration body,
            except that either party may bring a claim in small-claims court if eligible. Each party
            waives the right to participate in class, collective, or representative actions. If this
            arbitration clause is unenforceable in your jurisdiction, disputes will be brought
            exclusively in the state or federal courts located in California, U.S.
          </p>
          <p className={body}>
            These Terms and any dispute arising out of them are governed by the laws of the State of
            California, United States, excluding its conflict-of-laws rules.
          </p>
        </section>

        <section id="changes" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>15. Changes to These Terms</h2>
          <p className={body}>
            We may update these Terms from time to time. When we do, we will update the "Last
            Updated" date above and, for material changes, notify active players by email or in-app
            notice. Your continued use of the Service after changes take effect constitutes
            acceptance of the updated Terms. If you don't agree, stop using the Service and delete
            your account.
          </p>
        </section>

        <section id="contact" className="space-y-4 scroll-mt-8">
          <h2 className={h2}>16. Contact</h2>
          <p className={body}>Questions about these Terms?</p>
          <ul className={list}>
            <li>
              Legal:{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
            </li>
            <li>
              Support:{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
            </li>
            <li>
              Privacy:{' '}
              <a href="mailto:tavian@polymyth.quest" className={link}>
                tavian@polymyth.quest
              </a>
            </li>
            <li>
              Website:{' '}
              <a href="https://polymyth.quest" className={link}>
                polymyth.quest
              </a>
            </li>
          </ul>
        </section>

        <div className="border-t pt-8 mt-10 text-sm text-muted-foreground">
          <p>
            These Terms of Service are effective as of May 20, 2026. By using Polymyth on the web or
            through the iOS and Android Mobile Apps, you acknowledge that you have read, understood,
            and agree to be bound by these Terms.
          </p>
        </div>
      </div>
    </LegalPageLayout>
  );
}
